/**
 * T558 — is the navigation currently in flight the one this control started?
 *
 * The pure half of the pending-control idiom. React Router keeps the old screen mounted
 * for the whole wait, so a control that navigates and says nothing looks ignored — the
 * founder tapped the phase card several times for exactly that reason (T556). T556 gave
 * the tab bar and that card a pending state by comparing `navigation.location.pathname`
 * against a destination; this file is that comparison, extracted, so every control
 * T558 lights decides it the same way. Two mappers can disagree about what a
 * path means, and the disagreement shows up as two lit controls (R-T556-3, restated here
 * as R-T558-3).
 *
 * No React and no IO: the rule is a predicate over three locations, which is what makes it
 * testable without a router.
 */

export type NavLocation = {
  pathname: string;
  /** Leading `?` optional — both forms are accepted. */
  search?: string | null;
};

type Target = { pathname: string; search: string };

/** `/es/panel/` and `/es/panel` are the same place; `/` is not a trailing slash. */
function normalizePathname(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

/**
 * Order-insensitive, so `?tab=datos&x=1` matches `?x=1&tab=datos`. React Router builds the
 * query it navigates to; the control builds the query it points at. Nothing guarantees the
 * two serialize their params in the same order, and a control that fails to light because
 * of key order would be a bug nobody could read off the screen.
 */
function normalizeSearch(search: string | null | undefined): string {
  const raw = (search ?? "").replace(/^\?/, "");
  if (raw === "") return "";
  const params = new URLSearchParams(raw);
  params.sort();
  return params.toString();
}

/** Splits a `to` into the parts that identify a place. The hash is not one of them. */
function parseTarget(to: string): Target | null {
  const beforeHash = to.split("#", 1)[0];
  // Only absolute in-app paths can be compared against a location. A relative `to`
  // resolves against the route it is rendered under, which this predicate cannot see, so
  // it stays dark rather than guessing — a control that lights for the wrong navigation is
  // worse than one that does not light.
  if (!beforeHash.startsWith("/")) return null;
  const q = beforeHash.indexOf("?");
  return q === -1
    ? { pathname: normalizePathname(beforeHash), search: "" }
    : {
        pathname: normalizePathname(beforeHash.slice(0, q)),
        search: normalizeSearch(beforeHash.slice(q + 1)),
      };
}

function isAt(target: Target, location: NavLocation): boolean {
  if (normalizePathname(location.pathname) !== target.pathname) return false;
  // A control pointing at "the panel" is satisfied by any arrival at the panel. One
  // pointing at `?tab=datos` is not satisfied by `?tab=cuenta` — the account tabs share a
  // pathname and differ only there, so comparing pathnames alone would light all of them.
  if (target.search === "") return true;
  return normalizeSearch(location.search) === target.search;
}

/**
 * True while the in-flight navigation lands exactly where `to` points.
 *
 * @param navigationTo where React Router is going, or `null` when it is idle
 * @param to the control's own destination
 * @param current where she is now
 */
export function isNavigationTargeting(
  navigationTo: NavLocation | null | undefined,
  to: string,
  current: NavLocation,
): boolean {
  if (navigationTo == null) return false;
  const target = parseTarget(to);
  if (target == null) return false;
  // R-T558-4 — a control already on its own destination lights nothing. Without this,
  // tapping `Perfil` while on `Perfil` flickers into a treatment it drops a moment later.
  if (isAt(target, current)) return false;
  return isAt(target, navigationTo);
}
